import { CSSProperties, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { Loader } from 'components/organization';
import { BaseDataType, FetchDataArgs, SortDirection } from 'components/tables/data-table-context';
import { useOutsideClickHandler } from 'hooks';
import { Page } from 'types';

import { XOR } from 'utils';
import { TextField, TextFieldRef, ValidationStatus } from '../text-field';

/* alternative to searchable-multiselect
 * avoids fetching all data before input
 * accepts fetch-paginated function */

type Select<T> = XOR<
  { customSelect?: (option: T) => any },
  { handleSelect: Dispatch<SetStateAction<Map<number, T | null>>> }
>;

type ControlledAsyncSearchProps<T> = Select<T> & {
  disabled?: boolean;
  error?: string;
  id: string;
  label?: string;
  required?: boolean;
  sortField?: string & keyof T;
  fetchData: (args: FetchDataArgs) => Promise<Page<T>>;
  formatOption: (val?: T | null) => string;
  rowSelection: Map<number, T | null>;
  style?: CSSProperties;
  ['data-cy']?: string;
};

export const AsyncMultiselect = <T extends BaseDataType>({
  customSelect,
  fetchData,
  formatOption,
  handleSelect,
  rowSelection,
  sortField = 'createdDate',
  style,
  error,
  ...textFieldProps
}: ControlledAsyncSearchProps<T>) => {
  const [data, setData] = useState<T[]>([]);
  const [input, setInput] = useState<string>('');
  const [focused, setFocused] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const timeoutRef = useRef<NodeJS.Timeout>();
  const thisRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<TextFieldRef>(null);
  const warningRef = useRef<HTMLSpanElement>(null);
  useOutsideClickHandler(thisRef, () => setFocused(false));

  const setAdornment = (visible?: 'warning') => {
    console.log(visible);
    warningRef.current?.toggleAttribute('hidden', visible !== 'warning');
  };

  useEffect(() => {
    console.log(error);
    if (error) {
      setAdornment('warning');
      inputRef.current?.setCustomValidity(error);
    } else {
      setAdornment(undefined);
      inputRef.current?.setCustomValidity('');
    }
  }, [error]);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setLoading(true);
      fetchData({
        pageNum: 0,
        pageSize: 5,
        sortField,
        sortDirection: SortDirection.ASCENDING,
        ...(!!input && { filterString: `name=="${input}*"` })
      })
        .then(response => setData(response.content ?? []))
        .finally(() => setLoading(false));
    }, 500);
  }, [input, fetchData, setLoading, setData, sortField]);

  return (
    <div className="async-multiselect p-relative" onFocus={() => setFocused(true)} ref={thisRef} style={style}>
      <TextField
        {...textFieldProps}
        value={input}
        onChange={setInput}
        validate={async () => ({ status: ValidationStatus.DEFAULT })}
        allowSpaces
        ref={inputRef as any}
      />
      <span
        className="adornment material-icons icon-large warning margin-right-24px"
        ref={warningRef}
        hidden
      >warning</span>
      {focused && !textFieldProps.disabled && (
        <div className="popover-content min-width-fit mt-2 w-100">
          <Loader loading={loading}>
            {data.map((option, index) => (
              <label
                className="d-flex align-items-center g-2 p-2 white-space-nowrap"
                htmlFor={`option-${option.id}-id`}
                key={option.id}
                tabIndex={index}
                onClick={() =>
                  customSelect?.(option) ??
                  handleSelect?.(prev => new Map(prev).set(option.id, !prev.get(option.id) ? option : null))
                }
                data-value={option.id}
              >
                <input
                  className="option-checkbox"
                  id={`option-${option.id}-input`}
                  type="checkbox"
                  checked={!!rowSelection.get(option.id)}
                  readOnly
                />
                {formatOption(option)}
              </label>
            ))}
          </Loader>
        </div>
      )}
    </div>
  );
};

export default AsyncMultiselect;
